import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { analytics } from '../services/analytics';
import SEO from '../components/SEO';
import Footer from '../components/Footer';

// Icons
const CheckIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ZapIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);

const CameraIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
    <circle cx="12" cy="13" r="3" />
  </svg>
);

const XIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const WeddingPhotographyQuotingSoftware = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  
  // Structured data for Wedding Photography page
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product", 
    "name": "Wedding Photography Quoting Software by QuoteTier",
    "description": "Quoting software for wedding photographers. Sell coverage hours, albums, and engagement sessions with interactive proposals.",
    "category": "Software",
    "brand": {
      "@type": "Brand",
      "name": "QuoteTier"
    },
    "offers": {
      "@type": "Offer",
      "name": "Free Trial",
      "price": "0",
      "priceCurrency": "USD"
    },
    "audience": {
      "@type": "Audience",
      "audienceType": "Wedding Photographers, Portrait Photographers"
    }
  };

  useEffect(() => {
    analytics.track('WeddingPhoto Landing Page View');
  }, []);

  useEffect(() => {
    const controlHeader = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < lastScrollY || currentScrollY < 100) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', controlHeader);
    return () => window.removeEventListener('scroll', controlHeader);
  }, [lastScrollY]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO 
        title="Best Wedding Photography Quoting Software | Photography Proposals"
        description="Create beautiful wedding photography proposals that sell. Let couples choose their own hours, albums, and engagement sessions interactively."
        canonical="/wedding-photography-quote-software"
        structuredData={structuredData}
        ogTitle="Photography Software That Sells More Albums"
        ogDescription="Turn a 6-hour inquiry into a full-day package with album. Interactive quotes that showcase your portfolio."
        noindex={true}
      />
      {/* Header */}
      <header 
        className={`fixed top-4 left-1/2 z-50 w-[90%] max-w-4xl -translate-x-1/2 rounded-2xl border border-black/10 bg-white/95 shadow-lg transition-all duration-300 ease-in-out ${
          isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
        }`}
      >
        <div className="flex h-14 items-center justify-between px-4">
          <div className="flex items-center">
            <ZapIcon className="h-4 w-4 mr-2 text-primary" />
            <span className="font-instrument text-xl md:text-2xl">QuoteTier</span>
          </div>
          
          <nav className="hidden md:flex items-center space-x-6 text-sm font-medium">
            <Link to="/blog/" className="font-instrument text-black text-xl hover:text-gray-700 transition-colors">
              Blog
            </Link>
            <Link to="/login" className="font-instrument text-black text-xl hover:text-gray-700 transition-colors">
              Sign In
            </Link>
            <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'navigation', action: 'signup' })}>
              <Button className="font-instrument text-xl px-4 py-2 bg-black text-white hover:bg-gray-800 rounded-lg">
                Start Free
              </Button>
            </Link>
          </nav>
          
          <div className="md:hidden flex items-center space-x-2">
            <Link to="/blog/" className="font-instrument text-sm px-3 py-2 text-black hover:text-gray-700 transition-colors">
              Blog
            </Link>
            <Link to="/login" className="font-instrument text-sm px-3 py-2 text-black hover:text-gray-700 transition-colors">
              Sign In
            </Link>
            <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'mobile-navigation', action: 'signup' })}>
              <Button className="font-instrument text-sm px-3 py-2 bg-black text-white hover:bg-gray-800 rounded-lg">
                Free
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-32 md:py-40 lg:py-48">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="font-instrument text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-tight mb-6">
                Photography Quotes That <span className="text-primary font-bold">Sell The Story</span>
              </h1>
              <p className="font-jetbrains text-xl md:text-2xl text-gray-600 tracking-tight leading-relaxed mb-8">
                Your photos tell a story. Your quote should too. Let couples upgrade their own coverage, add albums, and book engagement sessions in one beautiful link.
              </p>
              <div className="font-instrument mt-12 flex flex-col sm:flex-row gap-6 justify-center items-center mb-6">
                <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'photo-hero', action: 'signup' })}>
                  <Button className="px-8 py-4 text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
                    Start Free — 3 Quotes/Mo
                  </Button>
                </Link>
                <Link to="/demo-quote/wedding-photography" onClick={() => analytics.track('Demo Clicked', { location: 'photo-hero', industry: 'wedding-photography' })}>
                  <Button className="px-8 py-4 text-xl font-semibold border-2 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg" variant="outline">
                    See Wedding Quote Demo →
                  </Button>
                </Link>
              </div>
              <p className="font-jetbrains mt-10 text-lg text-gray-500">
                No credit card required • Setup in 5 minutes • Works on mobile
              </p>
            </div>
          </div>
        </section>

        {/* Photography Quote Example */}
        <section className="py-16 md:py-24 bg-gray-50">
          <div className="container max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">
                6 Hours vs Full Day Coverage
              </h2>
              <p className="font-jetbrains text-lg text-gray-600">
                When couples see what they miss by choosing the basic package, they upgrade.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-3 max-w-5xl mx-auto">
              {/* Basic Package */}
              <Card className="relative border border-black transition-all">
                <CardHeader>
                  <div className="flex justify-between items-center mb-2">
                    <CardTitle className="font-instrument text-xl">Essential</CardTitle>
                    <span className="text-2xl font-bold">$2,800</span>
                  </div>
                  <CardDescription className="font-jetbrains">Ceremony & Portraits Only</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />6 Hours Coverage</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Online Gallery</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Digital Download</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Print Release</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Popular Package */}
              <Card className="relative border border-black transform scale-105">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-black text-white px-4 py-1 rounded-full text-sm font-semibold">Most Popular</span>
                </div>
                <CardHeader>
                  <div className="flex justify-between items-center mb-2">
                    <CardTitle className="font-instrument text-xl">The Storyteller</CardTitle>
                    <span className="text-2xl font-bold">$4,200</span>
                  </div>
                  <CardDescription className="font-jetbrains">Full Day + Engagement</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />8 Hours Coverage</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Engagement Session</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Second Shooter (4 hrs)</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />$200 Print Credit</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />USB Box</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Premium Package */}
              <Card className="relative border border-black">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-black text-white px-4 py-1 rounded-full text-sm font-semibold">Best Value</span>
                </div>
                <CardHeader>
                  <div className="flex justify-between items-center mb-2">
                    <CardTitle className="font-instrument text-xl">Heirloom</CardTitle>
                    <span className="text-2xl font-bold">$6,500</span>
                  </div>
                  <CardDescription className="font-jetbrains">All Inclusive + Album</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />10 Hours Coverage</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Fine Art Album (12x12)</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />2 Parent Albums</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Full Day Second Shooter</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Rehearsal Dinner Coverage</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Photography Value Props */}
        <section className="py-16 md:py-24">
          <div className="container max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">Built for Photographers</h2>
              <p className="font-jetbrains text-lg text-gray-600">Designed to showcase your art and protect your business.</p>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <Card className="border border-black">
                <CardHeader>
                  <CardTitle className="font-instrument text-xl">Album Sales</CardTitle>
                  <CardDescription className="font-jetbrains">It's hard to sell an album after the wedding. Include it in the "Heirloom" package so they buy it upfront.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Increase revenue per wedding</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Lock in print sales early</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="border border-black">
                <CardHeader>
                  <CardTitle className="font-instrument text-xl">Visual Portfolio</CardTitle>
                  <CardDescription className="font-jetbrains">Your quote is a mini-website. Embed your best shots directly into the package descriptions.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Emotional selling</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Reinforce your style</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="border border-black">
                <CardHeader>
                  <CardTitle className="font-instrument text-xl">Legal Contracts</CardTitle>
                  <CardDescription className="font-jetbrains">Includes model release, copyright terms, and cancellation policies. Signed digitally with the quote.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Protect your images</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Binding agreement</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="border border-black">
                <CardHeader>
                  <CardTitle className="font-instrument text-xl">Add-Ons Shop</CardTitle>
                  <CardDescription className="font-jetbrains">Let mom add "Parent Album (+$400)" or let the couple add "Rehearsal Coverage (+$500)" themselves.</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 font-jetbrains text-sm">
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Zero-pressure upselling</li>
                    <li className="flex items-center gap-2"><CheckIcon className="h-4 w-4" />Customizable totals</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-16 md:py-24 bg-gray-50">
          <div className="container max-w-5xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">
                QuoteTier vs PDF Pricing Guides
              </h2>
              <p className="font-jetbrains text-lg text-gray-600">
                PDFs are static. Your pricing should be dynamic.
              </p>
            </div>
            
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-6 px-6 font-instrument text-lg font-semibold text-gray-900 bg-gray-50">
                      Feature
                    </th>
                    <th className="text-center py-6 px-6 bg-black text-white">
                      <div className="flex flex-col items-center gap-2">
                        <span className="font-instrument text-lg font-bold">QuoteTier</span>
                      </div>
                    </th>
                    <th className="text-center py-6 px-6 font-instrument text-lg font-semibold text-gray-700 bg-gray-50">
                      PDF Guide
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-6 font-jetbrains font-medium">Click-to-Add Upgrades</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50/30">
                    <td className="py-4 px-6 font-jetbrains font-medium">Integrated Contract Signing</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="py-4 px-6 font-jetbrains font-medium">Retainer Collection</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50/30">
                    <td className="py-4 px-6 font-jetbrains font-medium">Mobile Optimized</td>
                    <td className="py-4 px-6 text-center"><CheckIcon className="h-5 w-5 mx-auto" /></td>
                    <td className="py-4 px-6 text-center"><XIcon className="h-5 w-5 text-gray-300 mx-auto" /></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-16 md:py-24 bg-gradient-to-r from-gray-900 to-black text-white">
          <div className="container max-w-4xl mx-auto px-4 text-center">
            <h2 className="font-instrument text-4xl md:text-5xl font-bold mb-6">
              Ready to Book More Weddings?
            </h2>
            <p className="font-jetbrains text-xl text-gray-200 mb-8">
              Join photographers using QuoteTier to increase their booking rate and average package value.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8">
              <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'photo-final-cta', action: 'signup' })}>
                <Button className="px-10 py-4 font-instrument text-xl font-semibold bg-white text-black hover:bg-gray-100 rounded-lg shadow-lg">
                  Start Free — 3 Quotes/Mo
                </Button>
              </Link>
              <Link to="/demo-quote/wedding-photography" onClick={() => analytics.track('Demo Clicked', { location: 'photo-final-cta', industry: 'wedding-photography' })}>
                <Button className="px-8 py-4 text-lg font-semibold border-2 border-white text-white hover:bg-white/10 rounded-lg" variant="outline">
                  Try Interactive Demo First →
                </Button>
              </Link>
            </div>
            
            <p className="font-jetbrains text-sm text-gray-400">
              No credit card required • Cancel anytime • Setup in minutes
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default WeddingPhotographyQuotingSoftware;
